/** 
 * @fileoverview AI API utilities for solarIQ
 * 
 * all AI-related fetch calls to /ai
 * returns parsed data
 * if failed returns null
 * 
 * components handle toast notifications and UI feedback
 * 
 */

import {AI_ENDPOINT, apiHeaders} from "./apiConfig.js"

/**
 * extractText:
 * function for safe text extraction from ai responses
 * 
 * @param {Object} data - raw api response  
 * @returns {string} - extracted and trimmed text
 * @private
 */
function extractText(data) {
    const text = data?.content?.[0]?.text;
    return (text && text.trim()) ? text.trim() : null;
}

/*
------------------------------------------------------------------------------------------------------------
LANDING PAGE FUNCTIONS
------------------------------------------------------------------------------------------------------------
*/

/**
 * generateNarrative:
 * generates personalized narrative about solar quote
 * 
 * @param {Object} calcResults - results from calcSystem
 * @param {string} firstName 
 * @param {string} city 
 * @param {boolean} hadUpload - whether user uploaded a bill or not
 * 
 * @returns {Promise<string>|null} - AI-gen narrative or null
 */
export async function generateNarrative(calcResults, firstName, city, hadUpload) {
    try {
        const sysPrompt = `You are a certified South African solar energy consultant writing a personalised solar report summary for a homeowner.
CRITICAL RULES:
- Use ONLY the exact numbers provided below. Do not invent, round differently, or change any figures.
- Every number must appear in Rands (R) exactly as given.
- Do not make up panel brands, battery brands, or installer names.
- Be warm, human, and specific to the SA market (Eskom tariff hikes, Section 12B).
- Write 3 short paragraphs. Max 200 words total.
- Use the client's first name.`;

        const usrPrompt = `Write a personalised solar ROI summary for ${firstName} in ${city}.

THESE ARE THE VERIFIED CALCULATED NUMBERS — USE THEM EXACTLY:
- Monthly Eskom bill: R${calcResults.bill}
- Estimated monthly usage: ${calcResults.monthlyKwh} kWh
- Current Eskom tariff used: R${calcResults.tariff}/kWh
- Province peak sun hours: ${calcResults.sunHours} hours/day
- Recommended system size: ${calcResults.sysKw} kW (${calcResults.panels} x 620W premium panels)
- Monthly solar generation: ${calcResults.monthlyGenKwh} kWh
- Solar coverage of usage: ${calcResults.coverageRatio}%
- Estimated gross system cost: R${calcResults.gross.toLocaleString()} (at R${calcResults.pricePerKw.toLocaleString()}/kW installed)
- Section 12B tax deduction benefit: R${calcResults.sec12b.toLocaleString()}
- Net cost after incentives: R${calcResults.net.toLocaleString()}
- 60-month finance repayment: R${calcResults.payment.toLocaleString()}/month
- Monthly electricity saving: R${calcResults.savings.toLocaleString()}
- Net monthly benefit from Day 1: R${calcResults.netMo.toLocaleString()} (saving minus repayment)
- Estimated payback period: ${calcResults.payback} years
- 25-year net savings: R${(calcResults.life25/1000).toFixed(0)}k
- Eskom cost over 25 years at 12% annual increases: R${(calcResults.util25/1000).toFixed(0)}k
- Annual CO₂ saving: ${calcResults.co2Saved} kg
${hadUpload ? "- Numbers derived from actual uploaded Eskom bill (most accurate)" : "- Numbers estimated from stated Monthly Spend"}

Write 3 paragraphs:
1. What the numbers show — system size, coverage, and what ${calcResults.coverageRatio}% solar coverage means practically (no more Eskom tariff increases impact, massive bill reduction)
2. The financial case — Day 1 cash flow positive at R${calcResults.netMo.toLocaleString()}/month net, Section 12B benefit of R${calcResults.sec12b.toLocaleString()}, 25-year savings of R${(calcResults.life25/1000).toFixed(0)}k vs paying Eskom R${(calcResults.util25/1000).toFixed(0)}k
3. Next step — note this is a market estimate, a certified adviser will do a site assessment to confirm exact pricing and system design`;


        const res = await fetch(AI_ENDPOINT, {
            method: "POST",
            headers: apiHeaders(),
            body: JSON.stringify({
                task: "text",
                max_tokens: 280,
                messages: [
                    {role: "system", content: sysPrompt},
                    {role: "user", content: usrPrompt}
                ] 
            })
        });

        if (!res.ok) {
            console.error("generateNarrative API error:", res.status);
            return fallbackNarrative(calcResults, firstName, city);
        }
        const data = await res.json();
        return extractText(data) ||  fallbackNarrative(calcResults, firstName, city);
    
    } catch (error) {
        console.error("Narrative generation failed.");
        return fallbackNarrative(calcResults, firstName, city);
    }

}

/**
 * fallbackNarrative:
 * for when AI unavailable
 * pure function, no API call
 * 
 * @param {Object} calcResults - results from calcSystem
 * @param {string} firstName
 * @param {string} city 
 * 
 * @returns {string} - fallback narrative text
 * @private
 */
function fallbackNarrative(calcResults, firstName, city) {
    return `${firstName}, based on your R${calcResults.bill}/month Eskom bill in ${city}, 
our system has calculated you use approximately ${calcResults.monthlyKwh} kWh per month at R${calcResults.tariff}/kWh
— placing your ideal system size at ${calcResults.sysKw} kW (${calcResults.panels} x 620W premium panels).

At ${calcResults.sunHours} peak sun hours per day in your area, this system will generate around ${calcResults.monthlyGenKwh} kWh monthly
— covering ${calcResults.coverageRatio}% of your current usage. That means R${calcResults.savings.toLocaleString()} less going to Eskom every single month, 
and no more Eskom tariff increases disruptions. With a 60-month finance payment of R${calcResults.payment.toLocaleString()}/month, 
you're R${calcResults.netMo.toLocaleString()} cash flow positive from Day 1.

The Section 12B tax deduction gives you R${calcResults.sec12b.toLocaleString()} back, bringing your net cost to R${calcResults.net.toLocaleString()}.
Over 25 years you save R${(calcResults.life25/1000).toFixed(0)}k vs paying Eskom an estimated R${(calcResults.util25/1000).toFixed(0)}k at 12% annual tariff increases.
These are MARKET ESTIMATES — a certified adviser will do a site visit to give you the exact figures for your roof and location.`;
}

/*
------------------------------------------------------------------------------------------------------------
COMMAND CENTER FUNCTIONS
------------------------------------------------------------------------------------------------------------
*/

/**
 * generateCloseScript:
 * generates closing script for sales rep/admin to reference
 * based on selected 'template' and full lead data
 * 
 * @param {string} template - closing script template/style selected
 * @param {Object} lead - current selected lead
 * 
 * @returns {Promise<string|null>} - generated script or null on failure
 */
export async function generateCloseScript(template, lead) {
    try {
        const res = await fetch(AI_ENDPOINT, {
            method: "POST",
            headers: apiHeaders(),
            body: JSON.stringify({
                task: "text",
                max_tokens: 280,
                messages: [{
                    role: "user",
                    content: `You are a certified solar sales closer. Generate a complete, personalized closing script using the "${template.name}" approach.

${lead ? `Lead: ${lead.name}, ${lead.city}
Monthly Bill: R${lead.monthlyBill} with ${lead.utility}
System: ${lead.systemSize}kW, Net Cost R${lead.netCost} after Section 12B deduction (R${lead.taxCredit})
Monthly Payment: R${lead.monthlyPayment} | Monthly Savings: R${lead.monthlySavings}
Roof: ${lead.roofAge}yr ${lead.roofType}, ${Math.round(lead.sqft * 0.0929)} m²
Source: ${lead.source}${lead.adSet ? ` (Ad: ${lead.adSet})` : ""}` : "Generic homeowner"}

Template: ${template.script}

Fill in ALL the numbers with real data as provided above. 
Make it sound completely natural and conversational. Include exact pauses, tone cues like "(pause, let them think)", and 
end with an open but firm question about the next steps. The intention is to create a convincing close at the end. Under 200 words.`
                }]
            })
        });

        if (!res.ok) {
            console.error("generateCloseScript API error:", res.status);
            return null;
        }

        const data = await res.json();
        return extractText(data);

    } catch {
        console.error("Failed to generate close script.");
        return null;
    }
}

/*
------------------------------------------------------------------------------------------------------------
SHARED FUNCTIONS (CHATBOX)
------------------------------------------------------------------------------------------------------------
*/

/** 
 * sendChatMsg:
 * sends message to AI assistant (chatbot interaction)
 * accounts for both landing (quote results) and admin (leads context) page modes
 * 
 * @param {Array} msgHistory - array of {role, content} message objects
 * @param {Object} [opts] 
 * @param {Object} [opts.results] - landing page quote results
 * @param {string} [opts.leadsCtx] - admin page: selected lead context summary
 * @param {Object|null} results
 * @returns {Promise<string|null>} - AI response text or null
 */
export async function sendChatMsg(msgHistory, {results = null, leadsCtx = null } = {}) {
    try { 
        const sysPrompt = leadsCtx
        ? `You are a solar sales assistant for a South African solar company.
Help the sales rep manage and convert their leads. Be concise and practical.
Current leads context: ${leadsCtx}`
        : `You are a friendly, certified South African solar consultant. Answer questions using ONLY the numbers provided below if available. 
Keep answers concise (under 120 words), warm, specific to SA. 
Reference Eskom, Rands, Section 12B, Eskom tariff increases where relevant.${results ? `
            
THIS CLIENT'S VERIFIED SOLAR ANALYSIS:
- Monthly Eskom bill: R${results.bill} | Usage: ${results.monthlyKwh} kWh/month
- Recommended system: ${results.sysKw} kW (${results.panels} panels)
- Solar generation: ${results.monthlyGenKwh} kWh/month (${results.coverageRatio}% of usage)
- Estimated system cost: R${results.gross.toLocaleString()} | After Section 12B: R${results.net.toLocaleString()}
- Monthly saving: R${results.savings.toLocaleString()} | Finance payment: R${results.payment.toLocaleString()}/mo
- Net monthly benefit: R${results.netMo.toLocaleString()} from Day 1
- Payback: ${results.payback} years | 25yr savings: R${(results.life25/1000).toFixed(0)}k

Always reference these exact numbers when answering questions about their system.` 
: " No analysis run yet — ask them about their bill amount first."}`;
        
        const res = await fetch(AI_ENDPOINT, {
            method: "POST",
            headers: apiHeaders(),
            body: JSON.stringify({
                task: "text",
                max_tokens: leadsCtx ? 600 : 200,
                messages: [
                    { role: "system", content: sysPrompt },
                    ...msgHistory
                ]
            })
        });

        if (!res.ok) {
            console.error("Chat API returned error status:", res.status);
            return null;
        }

        const data = await res.json();
        return extractText(data);

    } catch (error) {
        console.error("Chat request failed.");
        return null;
    } 
}

/**
 * handleObjection:
 * handles any possible customer objections with an ai response (for chatbot box)
 * accounts for both landing (quote results) and admin (lead object) contexts
 * 
 * @param {string} objection - present objection/question
 * @param {Object} [opts]
 * @param {Object} [opts.results] - landing page quote results
 * @param {Object} [opts.lead] - command center lead object
 * @param {string} [opts.fallback] - fallback in case of AI failure
 * 
 * @returns {Promise<string|null>} - AI response or null
 */
export async function handleObjection(objection, {results = null, lead = null, fallback = null} = {}) {
    try {
        const context = results 
        ? `Their situation: R${results.bill}/mo Eskom bill · ${results.sysKw}kW system · R${results.savings}/mo savings · Section 12B benefit R${results.sec12b}`
        : lead
        ? `Lead: ${lead.name}, ${lead.city}. Bill: R${lead.monthlyBill}/mo. Score: ${lead.score}.`
        : "";

        const res = await fetch(AI_ENDPOINT, {
            method: "POST",
            headers: apiHeaders(),
            body: JSON.stringify({
                task: "text",
                max_tokens: 140,
                messages: [

                    {
                        role: "system", 
                        content: `You are South Africa's top solar sales consultant.
Write a warm, helpful, honest response that addresses their concern with real SA numbers. 
Sound like a trusted adviser, not a salesperson. Under 100 words. Conversational tone.`
                    },    

                    {
                        role: "user",
                        content: `A homeowner asked/said: "${objection}"\n${context}`
                    }

                ]
            })
        });

        if (!res.ok) {
            console.error("Objection Handler API returned error status:", res.status);
            return fallback || null;
        }
        const data = await res.json();
        return extractText(data) || fallback || null;

    } catch (error) {
        console.error("handleObjection failed.");
        return fallback || null;
    }
}